// filepath: apps/web/src/features/admin/effects/HttpEndpointConflictDialog.tsx
//
// FR-UI-ADM-8 AC-4 -- modal dialog for HTTP 409 ENDPOINT_VERSION_MISMATCH.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { VersionConflictError } from "./types";

interface HttpEndpointConflictDialogProps {
  open: boolean;
  conflict: VersionConflictError | null;
  onRefetch: () => void;
  onDiscard: () => void;
  onClose: () => void;
}

export function HttpEndpointConflictDialog({
  open,
  conflict,
  onRefetch,
  onDiscard,
  onClose,
}: HttpEndpointConflictDialogProps): React.ReactElement | null {
  const { t } = useTranslation();
  if (!open || conflict === null) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="http-endpoint-conflict-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="http-endpoint-conflict-dialog"
    >
      <div className="w-full max-w-md rounded-md border border-border bg-background p-4 shadow-lg">
        <h2 id="http-endpoint-conflict-title" className="text-sm font-semibold">
          {t("admin.effects.dialog.conflict.title")}
        </h2>
        <p className="mt-2 text-xs text-muted-foreground">{conflict.message}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("admin.effects.dialog.conflict.server_version")}:{" "}
          {conflict.currentVersion}
        </p>
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onDiscard}
            data-testid="http-endpoint-conflict-discard"
          >
            {t("admin.effects.dialog.conflict.discard")}
          </button>
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={() => {
              onRefetch();
              onClose();
            }}
            data-testid="http-endpoint-conflict-refetch"
          >
            {t("admin.effects.dialog.conflict.refetch")}
          </button>
        </div>
      </div>
    </div>
  );
}
